import { Store } from '@/contexts/StoreContext';

/**
 * Currency mapping for locale formatting
 * Maps currency codes to their corresponding locales for better formatting
 */
const CURRENCY_LOCALES: Record<string, string> = {
  'NGN': 'en-NG', 
  /*
  'USD': 'en-US',
  'GHS': 'en-GH',
  'KES': 'en-KE',
  'ZAR': 'en-ZA',
  'UGX': 'en-UG',
  'XOF': 'fr-SN', // West African CFA Franc (Senegal)
  'XAF': 'fr-CM', // Central African CFA Franc (Cameroon)
  */
};

/**
 * Unified currency metadata list
 * Source of truth for code, name label, symbol and locale
 */
export type CurrencyCode = keyof typeof CURRENCY_LOCALES | 'EUR' | 'GBP';

export interface CurrencyMeta {
  code: string;
  label: string;
  symbol: string;
  locale: string;
}

export const CURRENCIES: CurrencyMeta[] = [
  { code: 'NGN', label: '🇳🇬 Nigerian Naira (NGN)', symbol: '₦', locale: CURRENCY_LOCALES['NGN'] },
  /*
  { code: 'USD', label: '🇺🇸 US Dollar (USD)', symbol: '$', locale: CURRENCY_LOCALES['USD'] },
  { code: 'GHS', label: '🇬🇭 Ghanaian Cedi (GHS)', symbol: '₵', locale: CURRENCY_LOCALES['GHS'] },
  { code: 'KES', label: '🇰🇪 Kenyan Shilling (KES)', symbol: 'KSh', locale: CURRENCY_LOCALES['KES'] },
  { code: 'ZAR', label: '🇿🇦 South African Rand (ZAR)', symbol: 'R', locale: CURRENCY_LOCALES['ZAR'] },
  { code: 'UGX', label: '🇺🇬 Ugandan Shilling (UGX)', symbol: 'USh', locale: CURRENCY_LOCALES['UGX'] },
  { code: 'XOF', label: '🇸🇳 West African CFA Franc (XOF)', symbol: 'CFA', locale: CURRENCY_LOCALES['XOF'] },
  { code: 'XAF', label: '🇨🇲 Central African CFA Franc (XAF)', symbol: 'FCFA', locale: CURRENCY_LOCALES['XAF'] },
  { code: 'EUR', label: '🇪🇺 Euro (EUR)', symbol: '€', locale: 'de-DE' },
  { code: 'GBP', label: '🇬🇧 British Pound (GBP)', symbol: '£', locale: 'en-GB' },
   */
];

/** Return currency meta by code with sensible defaults */
export function getCurrencyMeta(code?: string | null): CurrencyMeta {
  const fallback: CurrencyMeta = { code: 'NGN', label: '🇳🇬 Nigerian Naira (NGN)', symbol: '₦', locale: 'en-NG' };
  if (!code) return fallback;
  const found = CURRENCIES.find(c => c.code === code);
  return found || fallback;
}

/**
 * Format a price using the store's currency and appropriate locale
 * @param price - The price to format
 * @param store - The store object containing currency information
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted price string
 */
export function formatPrice(
  price: number, 
  store?: Pick<Store, 'currency'> | null,
  options?: Intl.NumberFormatOptions
): string {
  const { code: currency, locale } = getCurrencyMeta(store?.currency);
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...options
  }).format(price);
}


/**
 * Get the currency symbol for a given store
 * @param store - The store object containing currency information
 * @returns Currency symbol string
 */
export function getCurrencySymbol(store?: Pick<Store, 'currency'> | null): string {
  const meta = getCurrencyMeta(store?.currency);
  return meta.symbol;
}

/**
 * Get locale for a given currency
 * @param store - The store object containing currency information
 * @returns Locale string
 */
export function getCurrencyLocale(store?: Pick<Store, 'currency'> | null): string {
  return getCurrencyMeta(store?.currency).locale;
}

/**
 * Format a number with the store's locale (without currency symbol)
 * @param value - The number to format
 * @param store - The store object containing currency information
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted number string
 */
export function formatNumber(
  value: number,
  store?: Pick<Store, 'currency'> | null,
  options?: Intl.NumberFormatOptions
): string {
  const { locale } = getCurrencyMeta(store?.currency);
  
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Export a simple options array for UI selects to avoid duplication
 */
export const CURRENCY_OPTIONS = CURRENCIES.map(c => ({ id: c.code, label: c.label }));
